/**
 * PAPPO — ParentPanelPage
 * Dashboard parental (dados reais): gestão de filhos, pedidos de ligação
 * (aprovação gated) e definição do botão liga/desliga de aprovação.
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import {
  ShieldAlert,
  Users,
  ShieldCheck,
  UserPlus,
  Check,
  XCircle,
  Link2,
  MessagesSquare,
  Eye,
  ChevronLeft,
  Bell,
  IdCard,
  Clock,
  Pencil,
  Snowflake,
  Play,
  Upload,
} from 'lucide-react';
import {
  listChildren,
  createChild,
  updateChild,
  setChildFreeze,
  setChildSchedule,
  listConnectionRequests,
  approveConnection,
  rejectConnection,
  getChildConversations,
  getChildMessages,
  type Child,
  type ConnectionRequest,
} from '../services/parent';
import { getKycStatus, enviarKyc } from '../services/kyc';
import type { KycStatus } from '../types/kyc';
import {
  listAlerts,
  dismissAlert,
  resolveAlert,
  denounceFromAlert,
  cutContactFromAlert,
} from '../services/alerts';
import type { Conversation, Message } from '../types/message';
import type { Alert } from '../types/alert';
import { Avatar } from '../components/common/Avatar';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { MessageBubble } from '../components/chat/MessageBubble';
import { AlertCard } from '../components/alerts/AlertCard';
import { useSocket } from '../hooks/useSocket';
import { useNotification } from '../hooks/useNotification';
import { useNotifications } from '../contexts/NotificationsContext';
import styles from './ParentPanelPage.module.css';

/** Humor: chave → emoji + rótulo (para o selo no card do filho). */
const MOOD_UI: Record<string, { emoji: string; label: string }> = {
  feliz: { emoji: '😄', label: 'Feliz' },
  bem: { emoji: '🙂', label: 'Bem' },
  mais_ou_menos: { emoji: '😐', label: 'Mais ou menos' },
  triste: { emoji: '😢', label: 'Triste' },
  bravo: { emoji: '😠', label: 'Bravo' },
  ansioso: { emoji: '😰', label: 'Ansioso' },
};

/** Status de acesso AGORA (usa a hora local do dispositivo = fuso da família). */
function childAccessStatus(child: Child): { blocked: boolean; label: string } {
  if (child.frozen)
    return { blocked: true, label: 'Bloqueado agora — congelado (hora do jantar)' };
  if (child.schedule_enabled && child.allowed_start && child.allowed_end) {
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = child.allowed_start.split(':').map(Number);
    const [eh, em] = child.allowed_end.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    const within = start <= end ? cur >= start && cur <= end : cur >= start || cur <= end;
    return within
      ? { blocked: false, label: `Liberado agora — até ${child.allowed_end}` }
      : {
          blocked: true,
          label: `Bloqueado agora — fora do horário (libera às ${child.allowed_start})`,
        };
  }
  return { blocked: false, label: 'Liberado — sem restrição ativa' };
}

/** Selo com o efeito ATUAL do controle de acesso (verde = pode usar; vermelho = bloqueado). */
function AccessBadge({ child }: { child: Child }) {
  const st = childAccessStatus(child);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        fontSize: 12,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 999,
        background: st.blocked ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
        color: st.blocked ? '#fca5a5' : '#34d399',
        border: `1px solid ${st.blocked ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)'}`,
      }}
    >
      {st.blocked ? '🔴' : '🟢'} {st.label}
    </span>
  );
}

export function ParentPanelPage() {
  const [children, setChildren] = useState<Child[]>([]);
  const [requests, setRequests] = useState<ConnectionRequest[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unread, setUnread] = useState(0);

  const { on } = useSocket();
  const push = useNotification();
  const { refresh: refreshNotifications } = useNotifications();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ display_name: '', username: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edição do filho (nome / senha) pelo responsável
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ display_name: '', password: '' });
  const [editError, setEditError] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const startEdit = (child: Child) => {
    setEditingId(child.id);
    setEditForm({ display_name: child.display_name, password: '' });
    setEditError('');
  };

  const saveEdit = async (childId: number) => {
    setEditError('');
    const payload: { display_name?: string; password?: string } = {};
    if (editForm.display_name.trim()) payload.display_name = editForm.display_name.trim();
    if (editForm.password) payload.password = editForm.password;
    if (!payload.display_name && !payload.password) {
      setEditError('Nada para alterar.');
      return;
    }
    setEditBusy(true);
    const res = await updateChild(childId, payload);
    setEditBusy(false);
    if (res.success) {
      const kids = await listChildren();
      if (kids.success && kids.data) setChildren(kids.data.children);
      setEditingId(null);
    } else {
      setEditError(res.error ?? 'Não foi possível salvar.');
    }
  };

  // Controle de acesso por-filho (congelar + horários) — atualização otimista
  const [schedDraft, setSchedDraft] = useState<
    Record<number, { start: string; end: string }>
  >({});
  const [scheduleBusy, setScheduleBusy] = useState<number | null>(null);
  const [scheduleMsg, setScheduleMsg] = useState<
    { id: number; text: string; ok: boolean } | null
  >(null);
  const draftFor = (child: Child) =>
    schedDraft[child.id] ?? {
      start: child.schedule_enabled ? child.allowed_start ?? '08:00' : '08:00',
      end: child.schedule_enabled ? child.allowed_end ?? '20:00' : '20:00',
    };
  const patchDraft = (child: Child, patch: Partial<{ start: string; end: string }>) =>
    setSchedDraft((prev) => ({ ...prev, [child.id]: { ...draftFor(child), ...patch } }));

  const handleToggleFreeze = async (child: Child) => {
    const next = !child.frozen;
    setChildren((prev) => prev.map((c) => (c.id === child.id ? { ...c, frozen: next } : c)));
    const res = await setChildFreeze(child.id, next);
    if (!res.success) {
      setChildren((prev) =>
        prev.map((c) => (c.id === child.id ? { ...c, frozen: !next } : c))
      );
    }
  };

  const handleToggleSchedule = async (child: Child, enabled: boolean) => {
    const d = draftFor(child);
    setChildren((prev) =>
      prev.map((c) =>
        c.id === child.id
          ? { ...c, schedule_enabled: enabled, allowed_start: d.start, allowed_end: d.end }
          : c
      )
    );
    const res = await setChildSchedule(child.id, { enabled, start: d.start, end: d.end });
    if (!res.success) {
      setChildren((prev) =>
        prev.map((c) => (c.id === child.id ? { ...c, schedule_enabled: !enabled } : c))
      );
    }
  };

  const handleSaveSchedule = async (child: Child) => {
    const d = draftFor(child);
    setScheduleBusy(child.id);
    setChildren((prev) =>
      prev.map((c) =>
        c.id === child.id
          ? { ...c, schedule_enabled: true, allowed_start: d.start, allowed_end: d.end }
          : c
      )
    );
    const res = await setChildSchedule(child.id, {
      enabled: true,
      start: d.start,
      end: d.end,
    });
    setScheduleBusy(null);
    setScheduleMsg({
      id: child.id,
      text: res.success ? `✓ Salvo (${d.start}–${d.end})` : res.error ?? 'Erro ao salvar',
      ok: res.success,
    });
    setTimeout(
      () => setScheduleMsg((m) => (m && m.id === child.id ? null : m)),
      3000
    );
  };

  const timeInputStyle = {
    padding: '6px 8px',
    borderRadius: 8,
    border: '1px solid var(--border-color)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 13,
  };

  // Nível 01 (KYC) + cônjuge
  const [kycStatus, setKycStatus] = useState<KycStatus>('none');
  const [kycReason, setKycReason] = useState<string | null>(null);
  const [rgFile, setRgFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [kycError, setKycError] = useState('');
  const [kycSubmitting, setKycSubmitting] = useState(false);

  const verificado = kycStatus === 'approved';

  // Supervisão de conversas dos filhos (somente leitura)
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [childConversations, setChildConversations] = useState<Conversation[]>([]);
  const [convLoading, setConvLoading] = useState(false);
  const [openConv, setOpenConv] = useState<Conversation | null>(null);
  const [childMessages, setChildMessages] = useState<Message[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  const load = useCallback(async () => {
    const [kids, reqs, alertsRes, kyc] = await Promise.all([
      listChildren(),
      listConnectionRequests(),
      listAlerts(),
      getKycStatus(),
    ]);
    if (kids.success && kids.data) setChildren(kids.data.children);
    if (reqs.success && reqs.data) setRequests(reqs.data.requests);
    if (alertsRes.success && alertsRes.data) {
      setAlerts(alertsRes.data.alerts);
      setUnread(alertsRes.data.unread_count);
    }
    if (kyc.success && kyc.data) {
      setKycStatus(kyc.data.verification_status);
      setKycReason(kyc.data.reason);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Alertas ao vivo: o backend coloca o pai na sala user_{id} no connect.
  useEffect(() => {
    const off = on('alert:new', (...args) => {
      const alert = args[0] as Alert;
      setAlerts((prev) => [alert, ...prev]);
      setUnread((u) => u + 1);
    });
    return off;
  }, [on]);

  const handleDismissAlert = useCallback(async (alert: Alert) => {
    const wasPending = alert.status === 'pending';
    setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    if (wasPending) setUnread((u) => Math.max(0, u - 1));
    const res = await dismissAlert(alert.id);
    // Falha → repõe o alerta e o contador; senão ressincroniza o badge global.
    if (!res.success) {
      setAlerts((prev) => [alert, ...prev.filter((a) => a.id !== alert.id)]);
      if (wasPending) setUnread((u) => u + 1);
    } else {
      refreshNotifications();
    }
  }, [refreshNotifications]);

  /* Resolver (ação tomada) — mantém o alerta visível com status 'resolved'. */
  const handleResolveAlert = useCallback(async (alert: Alert) => {
    const prevStatus = alert.status;
    const wasPending = prevStatus === 'pending';
    setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, status: 'resolved' } : a)));
    if (wasPending) setUnread((u) => Math.max(0, u - 1));
    const res = await resolveAlert(alert.id);
    if (!res.success) {
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, status: prevStatus } : a)));
      if (wasPending) setUnread((u) => u + 1);
    } else {
      refreshNotifications();
    }
  }, [refreshNotifications]);

  /* Denunciar o contato adulto à moderação (F2) — vira 'resolved'. */
  const handleDenounceAlert = useCallback(async (alert: Alert) => {
    const nome = alert.contact?.name ?? 'este contato';
    if (!window.confirm(
      `Denunciar ${nome} à moderação do PAPPO? A mensagem sinalizada e a análise da IA vão como evidência.`
    )) return;
    const res = await denounceFromAlert(alert.id);
    if (res.success) {
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, status: 'resolved' } : a)));
      if (alert.status === 'pending') setUnread((u) => Math.max(0, u - 1));
      refreshNotifications();
    } else {
      window.alert(res.error || 'Não foi possível enviar a denúncia.');
    }
  }, [refreshNotifications]);

  /* Cortar o laço do filho com o contato (bloqueia + desconecta) — vira 'resolved'. */
  const handleCutContactAlert = useCallback(async (alert: Alert) => {
    const nome = alert.contact?.name ?? 'este contato';
    const filho = alert.child_name ?? 'seu filho';
    if (!window.confirm(
      `Cortar o contato entre ${filho} e ${nome}? Eles serão desconectados e não poderão mais trocar mensagens.`
    )) return;
    const res = await cutContactFromAlert(alert.id);
    if (res.success) {
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, status: 'resolved' } : a)));
      if (alert.status === 'pending') setUnread((u) => Math.max(0, u - 1));
      refreshNotifications();
    } else {
      window.alert(res.error || 'Não foi possível cortar o contato.');
    }
  }, [refreshNotifications]);

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.display_name.trim() || !form.username.trim() || !form.password) {
      setError('Preencha todos os campos');
      return;
    }
    if (form.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setSubmitting(true);
    const res = await createChild({
      display_name: form.display_name.trim(),
      username: form.username.trim(),
      password: form.password,
    });
    setSubmitting(false);
    if (res.success) {
      setForm({ display_name: '', username: '', password: '' });
      setShowForm(false);
      await load();
    } else {
      setError(res.error ?? 'Erro ao criar conta da criança');
    }
  };

  const handleKycUpload = async (e: FormEvent) => {
    e.preventDefault();
    setKycError('');
    if (!rgFile || !selfieFile) {
      setKycError('Envie a foto do RG e a selfie.');
      return;
    }
    setKycSubmitting(true);
    const res = await enviarKyc(rgFile, selfieFile);
    setKycSubmitting(false);
    if (res.success) {
      setRgFile(null);
      setSelfieFile(null);
      await load();
    } else {
      setKycError(res.error ?? 'Falha ao enviar os documentos');
    }
  };

  const handleApprove = useCallback(async (id: number) => {
    await approveConnection(id);
    await load();
    refreshNotifications();
  }, [load, refreshNotifications]);

  const handleReject = useCallback(async (id: number) => {
    await rejectConnection(id);
    await load();
    refreshNotifications();
  }, [load, refreshNotifications]);

  const handleSelectChild = useCallback(async (child: Child) => {
    setSelectedChild(child);
    setOpenConv(null);
    setChildMessages([]);
    setChildConversations([]);
    setConvLoading(true);
    const res = await getChildConversations(child.id);
    setConvLoading(false);
    setChildConversations(res.success && res.data ? res.data.conversations : []);
  }, []);

  const handleOpenConversation = useCallback(
    async (child: Child, conv: Conversation) => {
      setOpenConv(conv);
      setChildMessages([]);
      setMsgLoading(true);
      const res = await getChildMessages(child.id, conv.id);
      setMsgLoading(false);
      setChildMessages(res.success && res.data ? res.data.messages : []);
    },
    []
  );

  const summaryCards = [
    {
      label: 'Alertas',
      value: unread,
      icon: <ShieldAlert size={24} />,
      color: 'var(--pappo-high)',
      bgColor: 'rgba(239, 68, 68, 0.08)',
    },
    {
      label: 'Crianças Vinculadas',
      value: children.length,
      icon: <Users size={24} />,
      color: 'var(--pappo-secondary)',
      bgColor: 'rgba(245, 158, 11, 0.08)',
    },
    {
      label: 'Pedidos de Ligação',
      value: requests.length,
      icon: <Link2 size={24} />,
      color: 'var(--pappo-primary)',
      bgColor: 'var(--pappo-primary-50)',
    },
  ];

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <ShieldAlert size={28} className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>Painel Parental</h1>
            <p className={styles.subtitle}>
              Gerencie os seus filhos e aprove as conversas com segurança
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.grid}>
        {summaryCards.map((card) => (
          <div key={card.label} className={styles.summaryCard}>
            <div
              className={styles.cardIcon}
              style={{ color: card.color, backgroundColor: card.bgColor }}
            >
              {card.icon}
            </div>
            <div className={styles.cardContent}>
              <span className={styles.cardValue}>{card.value}</span>
              <span className={styles.cardLabel}>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Alertas de segurança — superfície mais importante, no topo */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Alertas{' '}
            {unread > 0 && (
              <span style={{ color: 'var(--pappo-high)' }}>({unread})</span>
            )}
          </h2>
          {push.supported && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Bell size={16} />}
              onClick={() => push.subscribe()}
              isLoading={push.isLoading}
              disabled={push.isSubscribed}
            >
              {push.isSubscribed ? 'Notificações ativas' : 'Ativar notificações'}
            </Button>
          )}
        </div>
        {alerts.length === 0 ? (
          <p className={styles.empty}>Nenhum alerta no momento.</p>
        ) : (
          <div className={styles.children}>
            {alerts.map((a) => (
              <AlertCard
                key={a.id}
                alert={a}
                onDismiss={handleDismissAlert}
                onResolve={handleResolveAlert}
                onDenounce={handleDenounceAlert}
                onCutContact={handleCutContactAlert}
              />
            ))}
          </div>
        )}
      </section>

      {/* Pedidos de Ligação */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Pedidos de Ligação</h2>
        {requests.length === 0 ? (
          <p className={styles.empty}>Nenhum pedido de ligação pendente.</p>
        ) : (
          <div className={styles.children}>
            {requests.map((r) => {
              // O menor está sempre em inviter_child_id/inviter_name. Se o pedido
              // foi iniciado pela outra parte (adulto→criança), o rótulo tem de
              // refletir que foi ELA quem convidou — não o filho. Sem initiator
              // conhecido (linhas antigas), mantém-se o texto padrão.
              const childInitiated =
                r.initiator_id == null || r.initiator_id === r.inviter_child_id;
              return (
              <div key={r.id} className={styles.childRow}>
                <Avatar name={r.invitee_name} size="md" />
                <div className={styles.childInfo}>
                  <span className={styles.childName}>
                    {r.inviter_name} ↔ {r.invitee_name}
                  </span>
                  <span className={styles.childUser}>
                    {childInitiated
                      ? `${r.inviter_name} (seu filho) quer conversar com ${r.invitee_name}`
                      : `${r.invitee_name} quer conversar com ${r.inviter_name} (seu filho)`}
                  </span>
                </div>
                <div className={styles.formActions} style={{ margin: 0 }}>
                  <Button size="sm" leftIcon={<Check size={14} />} onClick={() => handleApprove(r.id)}>
                    Aprovar
                  </Button>
                  <Button size="sm" variant="ghost" leftIcon={<XCircle size={14} />} onClick={() => handleReject(r.id)}>
                    Recusar
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Verificação de Identidade (Nível 01) */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Verificação de Identidade</h2>
          <span className={styles.readonlyBadge}>
            <IdCard size={13} /> Nível 01
          </span>
        </div>
        {verificado ? (
          <div className={styles.childRow}>
            <ShieldCheck size={22} style={{ color: 'var(--pappo-safe)' }} />
            <div className={styles.childInfo}>
              <span className={styles.childName}>Identidade verificada</span>
              <span className={styles.childUser}>
                Você pode adicionar seus filhos e 1 cônjuge.
              </span>
            </div>
          </div>
        ) : kycStatus === 'pending' ? (
          <div className={styles.childRow}>
            <Clock size={22} style={{ color: 'var(--pappo-secondary)' }} />
            <div className={styles.childInfo}>
              <span className={styles.childName}>Em análise</span>
              <span className={styles.childUser}>
                Seus documentos estão na fila de revisão de um responsável do PAPPO.
              </span>
            </div>
          </div>
        ) : (
          <form className={styles.addForm} onSubmit={handleKycUpload}>
            <p className={styles.hint}>
              Para adicionar uma criança ou um cônjuge, verifique sua identidade:
              envie uma foto do seu <strong>RG</strong> e uma <strong>selfie</strong>.
              Um responsável do PAPPO revisa manualmente.
            </p>
            {kycStatus === 'rejected' && kycReason && (
              <div className={styles.empty} role="alert" style={{ color: 'var(--pappo-high)' }}>
                Envio anterior rejeitado: {kycReason}. Reenvie os documentos.
              </div>
            )}
            {kycError && (
              <div className={styles.empty} role="alert" style={{ color: 'var(--pappo-high)' }}>
                {kycError}
              </div>
            )}
            <label className={styles.fileField}>
              <span className={styles.childUser}>Foto do RG</span>
              <div className={styles.fileControl}>
                <span className={styles.fileButton}>
                  <Upload size={15} /> Escolher arquivo
                </span>
                <span className={styles.fileName}>
                  {rgFile ? rgFile.name : 'Nenhum arquivo escolhido'}
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                className={styles.fileNative}
                onChange={(e) => setRgFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <label className={styles.fileField}>
              <span className={styles.childUser}>Selfie</span>
              <div className={styles.fileControl}>
                <span className={styles.fileButton}>
                  <Upload size={15} /> Escolher arquivo
                </span>
                <span className={styles.fileName}>
                  {selfieFile ? selfieFile.name : 'Nenhum arquivo escolhido'}
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                className={styles.fileNative}
                onChange={(e) => setSelfieFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <div className={styles.formActions}>
              <Button type="submit" isLoading={kycSubmitting} leftIcon={<IdCard size={16} />}>
                Enviar para verificação
              </Button>
            </div>
          </form>
        )}
      </section>

      {/* Meus Filhos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Meus Filhos</h2>
          {verificado && (
            <Button size="sm" leftIcon={<UserPlus size={16} />} onClick={() => setShowForm((v) => !v)}>
              Adicionar filho
            </Button>
          )}
        </div>

        {!verificado && (
          <p className={styles.empty}>
            Verifique sua identidade (Nível 01) para adicionar uma criança.
          </p>
        )}
        {showForm && (
          <form className={styles.addForm} onSubmit={handleSubmit}>
            <p className={styles.hint}>
              Você cria a conta do seu filho. Ele entrará no próprio aparelho com o{' '}
              <strong>nome de utilizador</strong> e a senha que definir.
            </p>
            {error && (
              <div className={styles.empty} role="alert" style={{ color: 'var(--pappo-high)' }}>
                {error}
              </div>
            )}
            <Input label="Nome da criança" placeholder="Ex: Lucas"
              value={form.display_name} onChange={(e) => update('display_name', e.target.value)} />
            <Input label="Nome de utilizador (login da criança)" placeholder="Ex: lucas_2015"
              value={form.username} onChange={(e) => update('username', e.target.value)} />
            <Input label="Senha" type="password" placeholder="Mín. 6 caracteres"
              value={form.password} onChange={(e) => update('password', e.target.value)} />
            <div className={styles.formActions}>
              <Button variant="ghost" type="button" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" isLoading={submitting}>Criar conta</Button>
            </div>
          </form>
        )}

        {children.length === 0 ? (
          <p className={styles.empty}>Ainda não adicionou nenhuma criança. Clique em "Adicionar filho".</p>
        ) : (
          <div className={styles.children}>
            {children.map((child) => (
              <div
                key={child.id}
                className={styles.childRow}
                style={{ flexWrap: 'wrap' }}
              >
                <Avatar name={child.display_name} src={child.avatar_url} size="md" />
                <div className={styles.childInfo}>
                  <span className={styles.childName}>{child.display_name}</span>
                  <span className={styles.childUser}>@{child.username}</span>
                  <span
                    style={{
                      marginTop: 4,
                      alignSelf: 'flex-start',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: child.is_shared
                        ? 'rgba(168,85,247,0.18)'
                        : 'rgba(255,255,255,0.07)',
                      color: child.is_shared ? '#c4b5fd' : 'var(--text-muted)',
                      border: `1px solid ${
                        child.is_shared ? 'rgba(168,85,247,0.4)' : 'var(--border-color)'
                      }`,
                    }}
                  >
                    {child.is_shared ? '🤝 Guarda compartilhada' : '👤 Meu filho'}
                  </span>
                  {child.mood_today && MOOD_UI[child.mood_today] && (
                    <span
                      style={{
                        marginTop: 4,
                        alignSelf: 'flex-start',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'var(--accent-glow)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      {MOOD_UI[child.mood_today].emoji} Humor de hoje:{' '}
                      {MOOD_UI[child.mood_today].label}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Pencil size={14} />}
                  onClick={() =>
                    editingId === child.id ? setEditingId(null) : startEdit(child)
                  }
                >
                  Editar
                </Button>

                {editingId === child.id && (
                  <div
                    style={{
                      flexBasis: '100%',
                      marginTop: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <Input
                      label="Nome do filho"
                      value={editForm.display_name}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, display_name: e.target.value }))
                      }
                    />
                    <Input
                      label="Nova senha (deixe em branco para manter a atual)"
                      type="password"
                      value={editForm.password}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, password: e.target.value }))
                      }
                      placeholder="mínimo 6 caracteres"
                      autoComplete="new-password"
                    />
                    {editError && (
                      <span style={{ color: '#fca5a5', fontSize: 13 }}>{editError}</span>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="sm" isLoading={editBusy} onClick={() => saveEdit(child.id)}>
                        Salvar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Controle de acesso: congelar ("hora do jantar") + horários */}
                <div
                  style={{
                    flexBasis: '100%',
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <AccessBadge child={child} />
                  {/* Congelar */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        ❄️ Congelar chat (hora do jantar)
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {child.frozen
                          ? 'Congelado — a criança não consegue enviar mensagens agora.'
                          : 'Pausa o envio de mensagens da criança na hora.'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant={child.frozen ? 'primary' : 'ghost'}
                      leftIcon={child.frozen ? <Play size={14} /> : <Snowflake size={14} />}
                      onClick={() => handleToggleFreeze(child)}
                    >
                      {child.frozen ? 'Descongelar' : 'Congelar'}
                    </Button>
                  </div>

                  {/* Horários de acesso */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={!!child.schedule_enabled}
                        onChange={(e) => handleToggleSchedule(child, e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: 'var(--accent-primary)' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          🕒 Horários de acesso
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Fora da janela, a criança não envia mensagens.
                        </span>
                      </div>
                    </label>
                    {child.schedule_enabled && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                          paddingLeft: 26,
                        }}
                      >
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Das</span>
                        <input
                          type="time"
                          value={draftFor(child).start}
                          onChange={(e) => patchDraft(child, { start: e.target.value })}
                          style={timeInputStyle}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>até</span>
                        <input
                          type="time"
                          value={draftFor(child).end}
                          onChange={(e) => patchDraft(child, { end: e.target.value })}
                          style={timeInputStyle}
                        />
                        <Button
                          size="sm"
                          isLoading={scheduleBusy === child.id}
                          onClick={() => handleSaveSchedule(child)}
                        >
                          Salvar horário
                        </Button>
                        {scheduleMsg?.id === child.id && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: scheduleMsg.ok ? 'var(--pappo-safe)' : '#fca5a5',
                            }}
                          >
                            {scheduleMsg.text}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Conversas dos Filhos (somente leitura) */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Conversas dos Filhos</h2>
          <span className={styles.readonlyBadge}>
            <Eye size={13} /> Somente leitura
          </span>
        </div>

        {children.length === 0 ? (
          <p className={styles.empty}>
            Adicione uma criança para acompanhar as conversas dela.
          </p>
        ) : (
          <>
            {/* Seletor de filho */}
            <div className={styles.childTabs}>
              {children.map((child) => (
                <button
                  key={child.id}
                  type="button"
                  className={`${styles.childTab} ${
                    selectedChild?.id === child.id ? styles.childTabActive : ''
                  }`}
                  onClick={() => handleSelectChild(child)}
                >
                  <Avatar name={child.display_name} src={child.avatar_url} size="sm" />
                  {child.display_name}
                </button>
              ))}
            </div>

            {!selectedChild ? (
              <p className={styles.empty}>
                Selecione um filho acima para ver as conversas dele.
              </p>
            ) : openConv ? (
              /* Visualizador de mensagens (somente leitura, sem caixa de envio) */
              <div className={styles.msgViewer}>
                <div className={styles.msgViewerHeader}>
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => setOpenConv(null)}
                    aria-label="Voltar à lista de conversas"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <Avatar
                    name={openConv.other_user?.display_name ?? 'Contato'}
                    src={openConv.other_user?.avatar_url}
                    size="sm"
                  />
                  <div className={styles.childInfo}>
                    <span className={styles.childName}>
                      {selectedChild.display_name} ↔{' '}
                      {openConv.other_user?.display_name ?? 'Contato'}
                    </span>
                    <span className={styles.childUser}>
                      <Eye size={12} /> Visualização do responsável — somente leitura
                    </span>
                  </div>
                </div>

                <div className={styles.msgList} role="list">
                  {msgLoading ? (
                    <p className={styles.empty}>Carregando mensagens…</p>
                  ) : childMessages.length === 0 ? (
                    <p className={styles.empty}>Nenhuma mensagem nesta conversa.</p>
                  ) : (
                    childMessages.map((m) => (
                      <MessageBubble
                        key={m.id}
                        message={m}
                        currentUserId={selectedChild.id}
                      />
                    ))
                  )}
                </div>
              </div>
            ) : convLoading ? (
              <p className={styles.empty}>Carregando conversas…</p>
            ) : childConversations.length === 0 ? (
              <p className={styles.empty}>
                {selectedChild.display_name} ainda não tem conversas.
              </p>
            ) : (
              <div className={styles.children}>
                {childConversations.map((conv) => (
                  <button
                    key={conv.id}
                    type="button"
                    className={styles.childRow}
                    style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    onClick={() => handleOpenConversation(selectedChild, conv)}
                  >
                    <Avatar
                      name={conv.other_user?.display_name ?? 'Contato'}
                      src={conv.other_user?.avatar_url}
                      size="md"
                    />
                    <div className={styles.childInfo}>
                      <span className={styles.childName}>
                        {conv.other_user?.display_name ?? 'Contato'}
                      </span>
                      <span className={styles.childUser}>
                        {conv.last_message?.content
                          ? conv.last_message.content.slice(0, 48)
                          : 'Sem mensagens ainda'}
                      </span>
                    </div>
                    <MessagesSquare
                      size={18}
                      style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
